import { useState } from 'react';
import {
  Box, Flex, VStack, Heading, Text, Input, Button, FormControl,
  FormLabel, FormErrorMessage, HStack, useToast, InputGroup,
  InputRightElement, IconButton,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/context/AuthContext';
import { RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import logo from '../assets/logo-white.png';

const MotionBox = motion(Box);

export default function LoginPage() {
  const [email, setemail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const validate = () => {
    const e = {};
    if (!email.trim()) e.email = 'email is required';
    if (!password) e.password = 'Password is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast({ title: 'Welcome back!', status: 'success', duration: 2000, isClosable: true });
      navigate(user.role === 'ADMIN' ? '/admin' : '/agent');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Invalid credentials. Please try again.';
      toast({ title: 'Login failed', description: msg, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex minH="100vh" bg="brand.black" align="center" justify="center" px={4}
      backgroundImage="linear-gradient(135deg, rgba(224,92,58,0.03) 0%, transparent 50%)"
    >
      {/* Decorative rings */}
      <Box position="absolute" top="10%" right="5%" w="400px" h="400px" borderRadius="50%"
        border="1px solid rgba(224,92,58,0.07)" pointerEvents="none" />
      <Box position="absolute" bottom="10%" left="5%" w="300px" h="300px" borderRadius="50%"
        border="1px solid rgba(224,92,58,0.05)" pointerEvents="none" />

      <MotionBox
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        w="full"
        maxW="420px"
      >
        {/* Logo */}
        <VStack spacing={1} mb={10} align="center">
          <img src={logo} alt="Logo" style={{ height: 44, marginBottom: 8 }} />
          <Text fontSize="8px" letterSpacing="0.4em" color="brand.muted" textTransform="uppercase">
            Portal Login
          </Text>
        </VStack>

        <Box bg="brand.charcoal" border="1px solid rgba(46,46,46,0.8)" p={8}>
          {/* Header */}
          <HStack spacing={3} mb={6}>
            <Box w="28px" h="1px" bg="brand.coral" />
            <Text fontSize="xs" letterSpacing="0.25em" color="brand.coral" textTransform="uppercase" fontWeight="600">
              Sign In
            </Text>
          </HStack>

          <Heading fontFamily="'Playfair Display', serif" fontWeight="400" fontSize="2xl" color="brand.cream" mb={6}>
            Welcome back.
          </Heading>

          <form onSubmit={handleSubmit}>
            <VStack spacing={5}>
              <FormControl isInvalid={!!errors.email}>
                <FormLabel fontSize="xs" letterSpacing="0.15em" textTransform="uppercase" color="brand.muted" mb={2}>
                  Email
                </FormLabel>
                <Input
                  variant="dark"
                  value={email}
                  onChange={(e) => setemail(e.target.value)}
                  placeholder="your@email.com"
                  autoComplete="email"
                />
                <FormErrorMessage fontSize="xs">{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel fontSize="xs" letterSpacing="0.15em" textTransform="uppercase" color="brand.muted" mb={2}>
                  Password
                </FormLabel>
                <InputGroup>
                  <Input
                    variant="dark"
                    type={showPass ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      color="brand.muted"
                      icon={showPass ? <RiEyeOffLine /> : <RiEyeLine />}
                      onClick={() => setShowPass(!showPass)}
                      aria-label="Toggle password"
                      _hover={{ color: 'brand.coral', bg: 'transparent' }}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage fontSize="xs">{errors.password}</FormErrorMessage>
              </FormControl>

              <Button
                variant="coral"
                w="full"
                type="submit"
                isLoading={loading}
                loadingText="Signing in..."
                mt={2}
              >
                Sign In
              </Button>
            </VStack>
          </form>
        </Box>

        <Text fontSize="xs" color="brand.muted" textAlign="center" mt={6} letterSpacing="0.1em">
          © 2025 Reliable Touch Services
        </Text>
      </MotionBox>
    </Flex>
  );
}
